/**
 * Reinstate the previous day's drops as new drafts for the current work date.
 * Copies prepared-drop records from workDate-1, filtered by category
 * (drafts / sent / scheduled / killed — all on by default), reusing the
 * copy-as-draft path. For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { History } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';

import { Button, Checkbox } from '@/components/ui';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { addDaysToIso, formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import { fetchPreparedDropsByCountry } from '@/hooks/smswiser-campaign-workspace/queries/queries-prepared-drops';
import { smswiserCampaignWorkspaceKeys } from '@/hooks/smswiser-campaign-workspace/keys';
import {
  classifyPreparedDrop,
  type PreparedDropCategory,
} from '@/features/smswiser-campaigns/components/PreparedDropsDraftsList/shared/filter';

const CATEGORIES: Array<{ key: PreparedDropCategory; label: string }> = [
  { key: 'draft', label: 'Drafts' },
  { key: 'sent', label: 'Sent' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'killed', label: 'Killed' },
];

interface ReinstateYesterdayButtonProps {
  countryKey: string;
  workDate: string;
  onReinstate: (drops: PreparedDrop[], targetWorkDate: string) => Promise<void>;
  isBusy?: boolean;
}

export const ReinstateYesterdayButton: FC<ReinstateYesterdayButtonProps> = ({
  countryKey,
  workDate,
  onReinstate,
  isBusy,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<PreparedDropCategory>>(
    () => new Set(CATEGORIES.map((c) => c.key)),
  );
  const [submitting, setSubmitting] = useState(false);
  const prevYmd = useMemo(() => addDaysToIso(workDate, -1), [workDate]);

  const { data: prevDrops = [], isLoading } = useQuery({
    queryKey: smswiserCampaignWorkspaceKeys.preparedDropsByCountry(countryKey, prevYmd),
    queryFn: () => fetchPreparedDropsByCountry(countryKey, prevYmd),
    enabled: open && Boolean(countryKey),
    staleTime: 5 * 60 * 1000,
  });

  const counts = useMemo(() => {
    const now = Date.now();
    const acc: Record<PreparedDropCategory, number> = { draft: 0, sent: 0, scheduled: 0, killed: 0 };
    for (const d of prevDrops) acc[classifyPreparedDrop(d, now)]++;
    return acc;
  }, [prevDrops]);

  const matching = useMemo(() => {
    const now = Date.now();
    return prevDrops.filter((d) => selected.has(classifyPreparedDrop(d, now)));
  }, [prevDrops, selected]);

  const toggle = (key: PreparedDropCategory) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleClick = async () => {
    if (matching.length === 0) return;
    setSubmitting(true);
    try {
      await onReinstate(matching, workDate);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button variant="outline" size="sm" className="gap-2 border-dashed" disabled={isBusy}>
          <History className="h-4 w-4" />
          Reinstate
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-[250] w-64 rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-lg"
        >
          <p className="text-xs text-muted-foreground">
            From <span className="font-medium text-foreground">{formatDateDMY(prevYmd)}</span> →{' '}
            <span className="font-medium text-foreground">{formatDateDMY(workDate)}</span>
          </p>
          <div className="mt-2 flex flex-col gap-0.5">
            {CATEGORIES.map((c) => (
              <label
                key={c.key}
                className="flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/50"
              >
                <span className="flex items-center gap-2">
                  <Checkbox checked={selected.has(c.key)} onCheckedChange={() => toggle(c.key)} />
                  {c.label}
                </span>
                <span className="text-xs text-muted-foreground">{counts[c.key]}</span>
              </label>
            ))}
          </div>
          <Button
            variant="default"
            size="sm"
            className="mt-3 w-full"
            disabled={matching.length === 0 || submitting || isLoading}
            onClick={handleClick}
          >
            {submitting ? 'Reinstating…' : `Reinstate (${matching.length})`}
          </Button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default ReinstateYesterdayButton;
